import React, { useMemo } from 'react'
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { Delete } from 'Components/DesignSystem/Iconography/Icons';

type SortOrderOption = 'ASC' | 'DESC';

type AttributeOption = {
    id: string;
    name: string;
};

type AttributeSortingItemProps = {
    item: AttributeSortItem;
    index: number;
    attributeOptions: AttributeOption[];
    allSelectedItems: AttributeSortItem[];
    onDelete: (id: string) => void;
    onAttributeChange: (id: string, attributeId: string, attributeName: string) => void;
    onOrderChange: (id: string, order: SortOrderOption) => void;
};

/**
 * One sorting row on DS controls (2026-09-08, phase 6): the two native selects
 * (with a hand-overlaid `MenuDownIcon` caret) → DS `Dropdown`s, the delete →
 * DS `IconButton` `danger-subtle` + `Delete` glyph, and the card re-inks
 * (`tw-bg-gray-100`/`tw-border-zinc-300`/bespoke rgba shadow → sunken/edge/
 * `tw-shadow-ds-sm`). The callback contracts are unchanged — including the
 * already-selected-attributes filtering and the keep-own-selection rule.
 */
export const AttributeSortingItem: React.FC<AttributeSortingItemProps> = ({
    item,
    index,
    attributeOptions,
    allSelectedItems,
    onDelete,
    onAttributeChange,
    onOrderChange,
}) => {
    // Filter out already selected attributes, but keep the current item's selection
    const filteredOptions = useMemo(() => {
        const selectedAttributeIds = allSelectedItems
            .filter(selectedItem => selectedItem.id !== item.id)
            .map(selectedItem => selectedItem.attribute_id)
            .filter(Boolean);

        return attributeOptions.filter(option =>
            !selectedAttributeIds.includes(option.id)
        );
    }, [attributeOptions, allSelectedItems, item.id]);

    const handleAttributeChange = (value: string | number) => {
        const selectedOption = attributeOptions.find(option => option.id === String(value));
        if (selectedOption) {
            onAttributeChange(item.id, selectedOption.id, selectedOption.name);
        }
    };

    const handleOrderChange = (value: string | number) => {
        onOrderChange(item.id, value as SortOrderOption);
    };

    return (
        <div className="tw-w-full tw-h-16 tw-relative" data-testid={`attribute-sorting-item-${index}`}>
            <div className="tw-w-full tw-h-16 tw-left-0 tw-top-0 tw-absolute tw-bg-surface-sunken tw-shadow-ds-sm tw-border tw-border-edge">
                <div className="tw-w-full tw-h-10 tw-py-2 tw-flex tw-items-center tw-justify-between tw-mt-3 tw-px-4">
                    <div className="tw-flex tw-items-center">
                        <div className="tw-w-8 tw-justify-start tw-text-ink tw-text-sm tw-font-semibold tw-ps-2">
                            {index + 1}
                        </div>

                        <div className="tw-w-64 tw-ml-2">
                            <Dropdown
                                label=""
                                aria-label="Sort attribute"
                                usePortal
                                placeholder="Select Attribute"
                                options={filteredOptions.map(option => ({ value: option.id, label: option.name }))}
                                value={item.attribute_id || undefined}
                                onChange={handleAttributeChange}
                                data-testid={`attribute-select-${index}`}
                            />
                        </div>
                    </div>

                    <div className="tw-w-48 tw-mx-8">
                        <Dropdown
                            label=""
                            aria-label="Sort order"
                            usePortal
                            options={[
                                { value: 'ASC', label: 'Ascending' },
                                { value: 'DESC', label: 'Descending' },
                            ]}
                            value={item.order || 'ASC'}
                            onChange={handleOrderChange}
                            data-testid={`order-select-${index}`}
                        />
                    </div>

                    <IconButton
                        variant="danger-subtle"
                        size="sm"
                        aria-label="Delete sorting"
                        onClick={() => onDelete(item.id)}
                        data-testid={`delete-button-${index}`}
                    >
                        <Delete />
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

export default AttributeSortingItem
